import React, { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import StatTileNew from '../ui/StatTileNew';
import { comparisonLabel } from '../../constants/visitsComparison';
import StatCard from '../ui/StatCard';
import { getAdminBadge, withAdminBadge } from '../../utils/getAdminBadge';
import { OPEN_FEATURE_ACCESS } from '../../constants/visitsConfig';
import VisitsSectorAnalysis from './VisitsSectorAnalysis';
import { isCountableVisit, isCompletedVisit, SPECIAL_TYPE_LABELS, VISIT_STATUS, statusColor } from '../../constants/visitsClassification';
import {
  UNIVERS_ALL,
  universLabel,
  countByUnivers,
  countByRetailerAndUnivers,
  stackEnds,
  universColorMap,
  UNIVERS_COLOR_SLOTS,
  OTHER_UNIVERS,
} from '../../constants/visitsUnivers';

// Single bar color for the retailer chart — not a categorical scale.
const RETAILER_BAR = { light: '#2a78d6', dark: '#3987e5' };

const isDark = () => document.documentElement.classList.contains('dark');
const inkPrimary = () => (isDark() ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)');
const gridInk = () => (isDark() ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');
const cardSurface = () => (isDark() ? '#1f2937' : '#ffffff');

const IconUsers = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconClock = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" />
  </svg>
);
const IconLayers = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 2 7l10 5 10-5-10-5Z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" />
  </svg>
);
const IconStore = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9h18l-1.5-5H4.5L3 9Z" /><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M9 20v-6h6v6" />
  </svg>
);
const IconBars = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20V10M18 20V4M6 20v-4" />
  </svg>
);

// Enregistrement des éléments Chart.js nécessaires
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

function VisitsSummary({ stats, username, allStaff = false, durationsAccess = OPEN_FEATURE_ACCESS, targetsAccess = OPEN_FEATURE_ACCESS, visits = [], currentUser, startDate, endDate, selectedProject, selectedShopSigns, fetchWithAuth, comparisonMode, universSplit = false, selectedUnivers = UNIVERS_ALL }) {
  // Every tile states what it is comparing against: with the comparison now
  // selectable, a tile left on StatTileNew's hardcoded "période précédente" default
  // would silently mislabel a year-on-year variation. Passed as a value rather than
  // through a wrapper component — a component defined in the render body is a new
  // type on every render, which would remount all ten tiles.
  const cmpLabel = comparisonLabel(comparisonMode);


  const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  // ---------------------------------------------------------------------------
  // Univers breakdown
  // ---------------------------------------------------------------------------
  // Computed on the COUNTABLE visits only, like every other figure on this card:
  // deleted rows, placeholders and "did not happen" are out of scope, and a
  // breakdown that included them would not add up to the tiles above it.
  const countableVisits = useMemo(() => visits.filter(isCountableVisit), [visits]);

  /**
   * The breakdown population: COMPLETED visits, so it reconciles with the tiles and
   * with every other indicator on this page.
   *
   * This matters more than it looks. The univers is only recorded when the promoter
   * fills the form, so a missed visit is always 'general' — on project 40922 in ISO
   * week 36, all 50 missed rows were 'general' and none of the 144 completed ones
   * were. Breaking down the countable set therefore drew a 26 % "no univers" slice
   * made entirely of visits that never happened, and summed to 194 under a tile
   * that read 144.
   */
  const breakdownVisits = useMemo(
    () => countableVisits.filter(isCompletedVisit),
    [countableVisits]
  );

  // Missed/not-done visits set aside from the breakdown, so the card can say so
  // rather than let the reader wonder why the donut is smaller than the period.
  const excludedFromBreakdown = countableVisits.length - breakdownVisits.length;

  // Hues follow univers IDENTITY, in the fixed distinctUnivers order — so filtering
  // or a change in ranking never repaints a univers.
  const universColors = useMemo(() => universColorMap(visits, isDark()), [visits]);

  // What the average is made of, inside the tile it explains — a single number
  // cannot be checked against the per-enseigne chart beside it. Admin only: the
  // per-univers split is a breakdown a client is shown nowhere else, and it reads
  // as an inconsistency next to their own single figure.
  const universDurations = universSplit ? (stats.durationByUnivers || []) : [];
  const durationSplit = currentUser.isadmin && universDurations.length > 1 ? (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="inline-flex items-center gap-1">{getAdminBadge()} dont</span>
      {universDurations.map((u) => (
        <span key={u.univers} className="inline-flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: universColors.get(u.univers) }}
          />
          {universLabel(u.univers)}
          <strong className="text-gray-700 dark:text-gray-300 tabular-nums">
            {u.minutes} min
          </strong>
        </span>
      ))}
    </span>
  ) : null;

  // Every univers with at least one visit, in identity order, each with its color.
  // The value table renders this list in full — nothing is hidden by the fold.
  const universBreakdown = useMemo(
    () => countByUnivers(breakdownVisits)
      .filter((u) => u.count > 0)
      .map((u, i) => ({
        ...u,
        color: universColors.get(u.univers),
        folded: i >= UNIVERS_COLOR_SLOTS,
      })),
    [breakdownVisits, universColors]
  );

  /**
   * What the donut draws: the first three univers as themselves, the rest summed
   * into one neutral "Autres" slice. Beyond three the palette has no hue that stays
   * distinguishable in dark mode, and a part-to-whole chart stops being readable
   * past a handful of segments anyway.
   */
  const universSlices = useMemo(() => {
    const named = universBreakdown.filter((u) => !u.folded);
    const folded = universBreakdown.filter((u) => u.folded);
    if (!folded.length) return named;

    const other = isDark() ? OTHER_UNIVERS.dark : OTHER_UNIVERS.light;
    return [...named, {
      univers: OTHER_UNIVERS.label,
      label: `${OTHER_UNIVERS.label} (${folded.length})`,
      count: folded.reduce((acc, u) => acc + u.count, 0),
      color: other,
    }];
  }, [universBreakdown]);

  const universTotal = useMemo(
    () => universBreakdown.reduce((acc, u) => acc + u.count, 0),
    [universBreakdown]
  );

  // Same bars, split by univers, so the weight of each univers inside a retailer
  // is readable. A stacked bar is judged on ADJACENT pairs, not all pairs, so the
  // three validated slots are enough here — and reusing the donut's exact colours
  // is what lets the two widgets on this row be read as one picture.
  const retailerStack = useMemo(
    () => countByRetailerAndUnivers(breakdownVisits),
    [breakdownVisits]
  );

  const retailerDatasets = useMemo(() => {
    const surface = cardSurface();
    const other = isDark() ? OTHER_UNIVERS.dark : OTHER_UNIVERS.light;
    const named = retailerStack.univers.slice(0, UNIVERS_COLOR_SLOTS);
    const folded = retailerStack.univers.slice(UNIVERS_COLOR_SLOTS);

    const series = [
      ...named.map((u) => retailerStack.retailers.map((r) => r.byUnivers.get(u) || 0)),
      ...(folded.length
        ? [retailerStack.retailers.map((r) =>
          folded.reduce((sum, u) => sum + (r.byUnivers.get(u) || 0), 0))]
        : []),
    ];

    const ends = stackEnds(retailerStack.retailers.length, series);

    const R = 4;
    const bar = (index, label, color) => ({
      label,
      data: series[index],
      backgroundColor: color,
      // The 2px surface gap that keeps two adjacent fills from reading as one.
      borderColor: surface,
      borderWidth: { left: 0, top: 0, bottom: 0, right: 2 },
      borderSkipped: false,
      borderRadius: (ctx) => {
        const end = ends[ctx.dataIndex] || {};
        return {
          topLeft: end.first === index ? R : 0,
          bottomLeft: end.first === index ? R : 0,
          topRight: end.last === index ? R : 0,
          bottomRight: end.last === index ? R : 0,
        };
      },
    });

    const datasets = named.map((u, i) => bar(i, universLabel(u), universColors.get(u)));

    // Folded into ONE series, exactly like the donut. Emitting one dataset per
    // univers past the palette put three identical grey segments side by side,
    // separated only by a 2px border, under three distinct legend entries.
    if (folded.length) {
      datasets.push(bar(named.length, `${OTHER_UNIVERS.label} (${folded.length})`, other));
    }
    return datasets;
  }, [retailerStack, universColors]);

  // 26px per bar keeps the labels legible; the wrapper scrolls past ~9 retailers
  // rather than squeezing them into a fixed height.
  const retailerChartHeight = Math.max(160, retailerStack.retailers.length * 26);
  // One dataset per univers only once there is something to split.
  const stackRetailers = retailerStack.univers.length > 1;

  // Says out loud why two visit counts can differ, so the gap reads as a rule
  // rather than a bug. Only stated when the doubling is actually visible.
  const universSubtitle = useMemo(() => {
    const perUnivers = stats.totalCompletedVisits ?? 0;
    const physical = stats.totalCompletedStoreVisits ?? 0;
    const parts = [];

    if (physical > 0 && perUnivers > physical) {
      const extra = perUnivers - physical;
      parts.push(`${extra} visite${extra > 1 ? 's' : ''} comptée${extra > 1 ? 's' : ''} dans plusieurs univers`);
    } else {
      parts.push('Sur les visites effectuées');
    }

    if (excludedFromBreakdown > 0) {
      parts.push(`${excludedFromBreakdown} non effectuée${excludedFromBreakdown > 1 ? 's' : ''} exclue${excludedFromBreakdown > 1 ? 's' : ''}`);
    }
    return parts.join(' · ');
  }, [stats.totalCompletedVisits, stats.totalCompletedStoreVisits, excludedFromBreakdown]);

  const reorganizedDurationByDay = useMemo(() => {
    return stats.avgDurationByDay
      ? [...stats.avgDurationByDay.slice(1), stats.avgDurationByDay[0]]
      : [0, 0, 0, 0, 0, 0, 0];
  }, [stats.avgDurationByDay]);

  const visitsCountByDay = useMemo(() => {
    try {
      if (!Array.isArray(visits) || visits.length === 0) {
        return [0, 0, 0, 0, 0, 0, 0];
      }

      const dayCounter = [0, 0, 0, 0, 0, 0, 0];

      // Only real, held store visits feed the "visites par jour" chart.
      visits.filter(isCountableVisit).forEach(visit => {
        try {
          if (visit.date && typeof visit.date.getDay === 'function') {
            const day = visit.date.getDay();
            const adjustedDay = day === 0 ? 6 : day - 1;
            dayCounter[adjustedDay] += 1;
          } else if (visit.plannedStart && typeof visit.plannedStart.getDay === 'function') {
            const day = visit.plannedStart.getDay();
            const adjustedDay = day === 0 ? 6 : day - 1;
            dayCounter[adjustedDay] += 1;
          }
        } catch (err) {
          console.error("Erreur lors du traitement d'une visite", err);
        }
      });

      return dayCounter;
    } catch (err) {
      console.error("Erreur lors du calcul du nombre de visites par jour", err);
      return [0, 0, 0, 0, 0, 0, 0];
    }
  }, [visits]);

  const showDurations = Boolean(durationsAccess?.visible);
  const durationsAdminOnly = Boolean(durationsAccess?.adminOnly);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-max">
      <StatCard
        icon={IconUsers}
        title={allStaff ? "Statistiques pour tout l'effectif" : `Statistiques pour ${username}`}
        subtitle={`Du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')} · ${comparisonLabel(comparisonMode, 'short')}`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
          {currentUser.isadmin && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              admin
              label="Visites planifiées"
              value={stats.totalPlannedVisits}
              previousValue={stats.previous?.totalPlannedVisits}
            />
          )}

          <StatTileNew comparisonLabel={cmpLabel} size="m"
            label="Visites effectuées"
            value={stats.totalCompletedVisits}
            previousValue={stats.previous?.totalCompletedVisits}
          />

          {currentUser.isadmin && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              admin
              label={`Taux de réalisation${stats.shouldShowToDate ? ` au ${endDate.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}` : ''}`}
              value={`${stats.completionRate}%`}
              previousValue={stats.previous?.completionRate != null ? `${stats.previous.completionRate}%` : null}
            />
          )}

          {currentUser.isadmin && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              admin
              label="Non effectuées (déplacement)"
              value={stats.totalDidNotHappen || 0}
              previousValue={stats.previous?.totalDidNotHappen ?? null}
              invertVariationColor
              description="Formulaire rempli : la visite n'a pas eu lieu"
            />
          )}

          <StatTileNew comparisonLabel={cmpLabel} size="m"
            label="Magasins visités"
            value={stats.uniqueStores}
            previousValue={stats.previous?.uniqueStores}
          />

          {allStaff && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              label="Effectif"
              value={stats.uniqueUsers || 0}
              previousValue={stats.previous?.uniqueUsers || null}
            />
          )}

          {allStaff && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              label="Jours de travail"
              value={stats.totalWorkDays || 0}
              previousValue={stats.previous?.totalWorkDays || null}
              additionalInfo={stats.uniqueUsers ? `${(stats.totalWorkDays / stats.uniqueUsers).toFixed(1)} jours/personne en moyenne` : ''}
            />
          )}
        </div>
      </StatCard>

      <StatCard
        icon={IconClock}
        title="Statistiques détaillées"
        subtitle={showDurations ? 'Cadence et durée des visites effectuées' : 'Cadence des visites effectuées'}
      >
        <div className="space-y-6">
          {showDurations && (
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              admin={durationsAdminOnly}
              label="Durée moyenne des visites"
              value={stats.averageDuration > 0
                ? `${Math.floor(stats.averageDuration / 60)}h${stats.averageDuration % 60 > 0
                  ? ` ${stats.averageDuration % 60}m`
                  : ''}`
                : 'N/A'}
              previousValue={stats.previous?.averageDuration > 0
                ? `${Math.floor(stats.previous.averageDuration / 60)}h${stats.previous.averageDuration % 60 > 0
                  ? ` ${stats.previous.averageDuration % 60}m`
                  : ''}`
                : null}
              additionalInfo={durationSplit}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <StatTileNew comparisonLabel={cmpLabel} size="m"
              label="Visites par jour"
              value={typeof stats.visitsPerDay === 'number' ? stats.visitsPerDay.toFixed(1) : (typeof stats.avgVisitsPerDay === 'number' ? stats.avgVisitsPerDay.toFixed(1) : 0)}
              previousValue={typeof stats.previous?.visitsPerDay === 'number'
                ? stats.previous.visitsPerDay.toFixed(1)
                : (typeof stats.previous?.avgVisitsPerDay === 'number' ? stats.previous.avgVisitsPerDay.toFixed(1) : null)}
              additionalInfo={`Sur ${stats.activeDays || 0} ${stats.activeDays === 1 ? 'jour' : 'jours'} actifs`}
            />

            <StatTileNew comparisonLabel={cmpLabel} size="m"
              label="Visites/jour/utilisateur"
              value={typeof stats.visitsPerDayPerUser === 'number' ? stats.visitsPerDayPerUser.toFixed(1) : (typeof stats.avgVisitsPerDayPerUser === 'number' ? stats.avgVisitsPerDayPerUser.toFixed(1) : 0)}
              previousValue={typeof stats.previous?.visitsPerDayPerUser === 'number'
                ? stats.previous.visitsPerDayPerUser.toFixed(1)
                : (typeof stats.previous?.avgVisitsPerDayPerUser === 'number' ? stats.previous.avgVisitsPerDayPerUser.toFixed(1) : null)}
              additionalInfo={`${stats.uniqueUsers || 0} ${stats.uniqueUsers === 1 ? 'utilisateur' : 'utilisateurs'} actifs`}
            />
          </div>

          {currentUser.isadmin && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Répartition des visites {getAdminBadge()}</h4>
              {(() => {
                // Colors from the shared status palette, so this bar, the calendar
                // and the lexicon cannot drift apart again.
                const segments = [
                  { v: stats.totalCompletedVisits || 0, c: statusColor(VISIT_STATUS.COMPLETED) },
                  { v: stats.totalMissedVisits || 0, c: statusColor(VISIT_STATUS.MISSED) },
                  { v: stats.totalDidNotHappen || 0, c: statusColor(VISIT_STATUS.NOT_DONE) },
                  { v: stats.totalDeletedVisits || 0, c: statusColor(VISIT_STATUS.CANCELLED) },
                ];
                const total = segments.reduce((a, b) => a + b.v, 0) || 1;
                return (
                  <div className="flex h-2.5 w-full rounded-full overflow-hidden mb-3 bg-gray-100 dark:bg-gray-700">
                    {segments.map((s, i) => s.v > 0 && (
                      <div key={i} style={{ width: `${(s.v / total) * 100}%`, backgroundColor: s.c }} />
                    ))}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border dark:border-green-800/30">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Effectuées:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{stats.totalCompletedVisits || 0}</span>
                  </div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded border dark:border-amber-800/30">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Manquées:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{stats.totalMissedVisits || 0}</span>
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded border dark:border-purple-800/30">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300" title="Formulaire répondu mais « la visite n'a pas eu lieu »">Non effectuées:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{stats.totalDidNotHappen || 0}</span>
                  </div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border dark:border-red-800/30">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300" title="Signal du churn de modifications sur le compte">Supprimées:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{stats.totalDeletedVisits || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentUser.isadmin && stats.totalSpecialVisits > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Visites spéciales (hors stats) {getAdminBadge()}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Placeholders/agendas exclus des analyses ({stats.totalSpecialVisits} au total).
              </p>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(stats.specialVisitsByType || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <div key={type} className="bg-gray-50 dark:bg-gray-700/40 p-3 rounded border dark:border-gray-600">
                      <div className="flex justify-between">
                        <span className="text-gray-700 dark:text-gray-300">{SPECIAL_TYPE_LABELS[type] || type}:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </StatCard>

      {/* Two sibling cards, so the enclosing 2-column grid lays them side by side
          and the existing "Durée moyenne" / "visites par jour" pair falls on the
          next row. They used to be one card, which put a nested 2-column grid
          inside a half-width cell and squeezed both charts. */}
      {universSplit && (
        <StatCard
          icon={IconLayers}
          title="Répartition par univers"
          subtitle={universSubtitle}
        >
          {/* The triptych. Shown whichever univers is selected: with one chosen the
              two counts converge, which confirms the doubling rather than leaving
              the reader to wonder where it went. */}
          {/* Two tiles, not three: the univers count and their names are already
              in the filter bar above, and the donut beside this row draws them. */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <StatTileNew
              size="s"
              label="Visites (univers)"
              value={stats.totalCompletedVisits ?? 0}
              description="Une visite couvrant deux univers compte dans chacun."
            />
            <StatTileNew
              size="s"
              label="Visites magasin"
              value={stats.totalCompletedStoreVisits ?? 0}
              description="Déplacements réels, indépendamment des univers."
            />
          </div>

          {selectedUnivers === UNIVERS_ALL ? (
            <div className="flex items-center gap-5">
              {/* The value table is not decoration: the light aqua sits under 3:1 on
                  white, so identity must not rest on color alone. */}
              <div className="h-40 w-40 flex-none">
                <Doughnut
                  data={{
                    labels: universSlices.map((u) => u.label || universLabel(u.univers)),
                    datasets: [{
                      data: universSlices.map((u) => u.count),
                      backgroundColor: universSlices.map((u) => u.color),
                      borderColor: cardSurface(),
                      borderWidth: 2,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '58%',
                    plugins: {
                      // Identity lives in the table beside the chart, which carries
                      // the values too — a legend box would repeat it without them.
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const count = ctx.raw || 0;
                            const share = universTotal > 0 ? Math.round((count / universTotal) * 100) : 0;
                            return `${ctx.label} : ${count} visite${count > 1 ? 's' : ''} (${share} %)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
              <ul className="min-w-0 flex-1 space-y-2 text-left">
                {universBreakdown.map((u) => (
                  <li key={u.univers} className="flex items-baseline gap-2 text-sm">
                    <span
                      className="w-2.5 h-2.5 rounded-sm flex-none translate-y-0.5"
                      style={{ backgroundColor: u.color }}
                      aria-hidden
                    />
                    <span className="text-gray-700 dark:text-gray-300 truncate">{universLabel(u.univers)}</span>
                    {/* Fixed width so the counts form a column: label lengths differ
                        ("Blanc" vs "Tous univers"), and margin-left:auto alone left
                        them ragged. */}
                    <span className="ml-auto font-semibold text-gray-900 dark:text-gray-100 tabular-nums w-12 text-right">{u.count}</span>
                    <span className="text-gray-500 dark:text-gray-400 tabular-nums w-10 text-right">
                      {universTotal > 0 ? Math.round((u.count / universTotal) * 100) : 0} %
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-left">
              Répartition masquée : un seul univers est sélectionné, le graphe
              n'aurait qu'une part. Repassez sur « Tous univers » pour la voir.
            </p>
          )}
        </StatCard>
      )}

      {/* Its own widget, and shown whatever the univers filter says — unlike the
          donut, a magnitude comparison between enseignes stays meaningful when the
          rows are narrowed to one univers. */}
      {universSplit && (
        <StatCard icon={IconStore} title="Visites par enseigne">
          <div className="overflow-y-auto" style={{ maxHeight: 260 }}>
            <div style={{ height: retailerChartHeight }}>
              <Bar
                data={{
                  labels: retailerStack.retailers.map((r) => r.retailer),
                  datasets: stackRetailers ? retailerDatasets : [{
                    label: 'Visites',
                    data: retailerStack.retailers.map((r) => r.total),
                    backgroundColor: isDark() ? RETAILER_BAR.dark : RETAILER_BAR.light,
                    borderRadius: { topRight: 4, bottomRight: 4 },
                    borderSkipped: false,
                  }],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  // Chart.js defaults to nearest/intersect, so a stacked tooltip
                  // would receive ONE segment — and the "Total" footer would
                  // print that segment's value under a label promising a total.
                  interaction: stackRetailers
                    ? { mode: 'index', intersect: false }
                    : { mode: 'nearest', intersect: true },
                  scales: {
                    x: {
                      beginAtZero: true,
                      stacked: stackRetailers,
                      ticks: { color: inkPrimary(), precision: 0 },
                      grid: { color: gridInk() },
                    },
                    y: {
                      stacked: stackRetailers,
                      ticks: { color: inkPrimary(), autoSkip: false },
                      grid: { display: false },
                    },
                  },
                  plugins: {
                    // A legend is required the moment there are two series;
                    // with one, the card title already names it.
                    legend: stackRetailers
                      ? {
                          position: 'bottom',
                          labels: {
                            color: inkPrimary(),
                            boxWidth: 10,
                            boxHeight: 10,
                            usePointStyle: true,
                            pointStyle: 'circle',
                          },
                        }
                      : { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          const n = ctx.raw;
                          const label = `${n} visite${n > 1 ? 's' : ''}`;
                          return stackRetailers ? `${ctx.dataset.label} : ${label}` : label;
                        },
                        // Read from the row, not re-summed: the same number that
                        // sorts the bars, so the two can never disagree.
                        footer: (items) => {
                          if (!stackRetailers || !items.length) return undefined;
                          const row = retailerStack.retailers[items[0].dataIndex];
                          return row ? `Total : ${row.total} visites` : undefined;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </StatCard>
      )}

      {showDurations && (
        <StatCard
          icon={IconClock}
          title={withAdminBadge('Durée moyenne des visites par jour', durationsAdminOnly)}
        >
          <div className="h-48">
            <Bar
              data={{
                labels: weekDays,
                datasets: [
                  {
                    label: 'Durée moyenne (min)',
                    data: reorganizedDurationByDay,
                    backgroundColor: '#3498db',
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Minutes',
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    },
                    ticks: {
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    },
                    grid: {
                      color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    }
                  },
                  x: {
                    ticks: {
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    },
                    grid: {
                      color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    }
                  }
                },
                plugins: {
                  legend: {
                    labels: {
                      color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.raw || 0;
                        if (value === 0) return 'Aucune visite';
                        return `Durée moyenne: ${Math.floor(value / 60)}h${value % 60 > 0 ? ` ${value % 60}m` : ''}`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </StatCard>
      )}

      {/* Spans the row when the per-day duration card it pairs with is closed. */}
      <StatCard
        icon={IconBars}
        title="Nombre total de visites par jour de la semaine"
        className={showDurations ? '' : 'lg:col-span-2'}
      >
        <div className="h-48">
          <Bar
            data={{
              labels: weekDays,
              datasets: [
                {
                  label: 'Nombre de visites',
                  data: visitsCountByDay,
                  backgroundColor: '#2ecc71',
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  min: 0,
                  title: {
                    display: true,
                    text: 'Nombre de visites',
                    color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                  },
                  ticks: {
                    color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                    precision: 0,
                    stepSize: 1,
                  },
                  grid: {
                    color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  }
                },
                x: {
                  ticks: {
                    color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                  },
                  grid: {
                    color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  }
                }
              },
              plugins: {
                legend: {
                  labels: {
                    color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
                  }
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const value = context.raw || 0;
                      if (value === 0) return 'Aucune visite';
                      return `Nombre de visites: ${value}`;
                    }
                  }
                }
              }
            }}
          />
        </div>
      </StatCard>

      <VisitsSectorAnalysis
        visits={visits}
        startDate={startDate}
        endDate={endDate}
        selectedProject={selectedProject}
        selectedShopSigns={selectedShopSigns}
        fetchWithAuth={fetchWithAuth}
        currentUser={currentUser}
        targetsAccess={targetsAccess}
        durationsAccess={durationsAccess}
      />
    </div>
  );
}

export default VisitsSummary;